import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Check, Trash2, RefreshCw, ExternalLink, AlertTriangle } from 'lucide-react';
import { Feed, FeedStatus, FeedLastRefresh } from '../../types';
import { Translations, Language } from '../../i18n/translations';

interface ExampleFeed {
  name: string;
  url: string;
}

interface RemoteFeedEntry {
  publication_name: string;
  publication_website_uri: string;
  publication_rss_feed_uri: string;
  category?: string;
  bot_protection?: boolean;
}

interface FeedManagerProps {
  feeds: Feed[];
  feedStatus: FeedStatus;
  feedLastRefresh: FeedLastRefresh;
  newFeedUrl: string;
  setNewFeedUrl: (url: string) => void;
  newFeedName: string;
  setNewFeedName: (name: string) => void;
  onAddFeed: () => void;
  onAddFeeds: (feeds: Feed[]) => void;
  onRemoveFeed: (url: string) => void;
  onReloadFeed: (feed: Feed) => void;
  error: string;
  t: Translations;
  language: Language;
}

const isFeedFailed = (status: string | undefined): boolean => {
  if (!status) return false;
  return status.startsWith('✗') || status.toLowerCase().includes('failed') || status.toLowerCase().includes('error');
};

const isFeedLoading = (status: string | undefined): boolean => {
  if (!status) return false;
  return status.toLowerCase().includes('loading') || status.toLowerCase().includes('retrying');
};

const formatRelativeTime = (timestamp: number, now: number, t: Translations): string => {
  const diffMs = now - timestamp;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);

  if (diffMins < 1) return t.justNow;
  if (diffMins < 60) return `${diffMins}${t.minutesAgo}`;
  if (diffHours < 24) return `${diffHours}${t.hoursAgo}`;
  return new Date(timestamp).toLocaleDateString();
};

export const FeedManager: React.FC<FeedManagerProps> = ({
  feeds,
  feedStatus,
  feedLastRefresh,
  newFeedUrl,
  setNewFeedUrl,
  newFeedName,
  setNewFeedName,
  onAddFeed,
  onAddFeeds,
  onRemoveFeed,
  onReloadFeed,
  error,
  t,
  language,
}) => {
  const [exampleFeeds, setExampleFeeds] = useState<ExampleFeed[]>([]);
  const [loadingFeeds, setLoadingFeeds] = useState(true);
  const [showAllFeeds, setShowAllFeeds] = useState(false);
  const [selectedExampleUrls, setSelectedExampleUrls] = useState<Set<string>>(new Set());
  const [now, setNow] = useState<number>(() => Date.now());

  // Lowercase URL set of already-subscribed feeds — used to disable the
  // "Add" tile for a recommended source the user already has.
  const subscribedUrls = useMemo(
    () => new Set(feeds.map(f => f.url.toLowerCase())),
    [feeds],
  );

  const toggleExample = (url: string): void => {
    setSelectedExampleUrls(prev => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url);
      else next.add(url);
      return next;
    });
  };

  const commitSelected = (): void => {
    const toAdd = exampleFeeds.filter(f => selectedExampleUrls.has(f.url));
    if (toAdd.length === 0) return;
    onAddFeeds(toAdd);
    setSelectedExampleUrls(new Set());
  };

  // Drop selections that just became subscribed (e.g. after a commit) so
  // the count never lies. Pure derived value, no extra state needed.
  const selectedCount = useMemo(
    () => exampleFeeds.filter(f => selectedExampleUrls.has(f.url) && !subscribedUrls.has(f.url.toLowerCase())).length,
    [exampleFeeds, selectedExampleUrls, subscribedUrls],
  );

  const commitLabel = selectedCount === 0
    ? t.onboardingAddNone
    : (selectedCount === 1 ? t.onboardingAddOne : t.onboardingAddMany.replace('{count}', String(selectedCount)));

  // Local 60s ticker so per-feed "5m ago" labels actually update while the
  // panel is open. Scoped to this component so it doesn't re-render the app.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const fetchExampleFeeds = async () => {
      try {
        const countryCode = language === 'pl' ? 'POL' : 'GBR';
        const response = await fetch(`/api/feed-list?country=${countryCode}`);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const countryFeeds: RemoteFeedEntry[] = await response.json();

        // Filter out feeds with bot protection and deduplicate by name
        const seen = new Set<string>();
        const feeds: ExampleFeed[] = [];

        for (const entry of countryFeeds) {
          if (entry.bot_protection) continue;
          if (!entry.publication_rss_feed_uri) continue;

          // Use base publication name (without category)
          const baseName = entry.publication_name;
          if (seen.has(baseName)) continue;
          seen.add(baseName);

          feeds.push({
            name: baseName,
            url: entry.publication_rss_feed_uri,
          });
        }

        setExampleFeeds(feeds);
        setShowAllFeeds(false);
      } catch {
        // Silent fail
      } finally {
        setLoadingFeeds(false);
      }
    };

    fetchExampleFeeds();
  }, [language]);

  return (
    <>
      <div className="bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 p-3 sm:p-4 mb-4" role="form" aria-label="Add custom RSS feed">
        <h3 className="font-bold mb-2 text-sm sm:text-base text-gray-900 dark:text-white">{t.addCustomFeed}</h3>
        {error && (
          <div className="bg-red-100 dark:bg-red-900 border-2 border-red-400 dark:border-red-600 text-red-700 dark:text-red-300 px-3 sm:px-4 py-2 mb-4 text-sm" role="alert">
            {error}
          </div>
        )}
        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-3">
          <strong className="dark:text-gray-300">Note:</strong> {t.feedNote}
        </p>
        <div className="mb-2">
          <label htmlFor="feed-name" className="sr-only">{t.feedNamePlaceholder}</label>
          <input
            id="feed-name"
            type="text"
            value={newFeedName}
            onChange={(e) => setNewFeedName(e.target.value)}
            placeholder={t.feedNamePlaceholder}
            className="w-full px-3 sm:px-4 py-2 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 text-sm"
            aria-describedby="feed-note"
          />
        </div>
        <div className="flex gap-2">
          <label htmlFor="feed-url" className="sr-only">{t.feedUrlPlaceholder}</label>
          <input
            id="feed-url"
            type="url"
            value={newFeedUrl}
            onChange={(e) => setNewFeedUrl(e.target.value)}
            placeholder={t.feedUrlPlaceholder}
            className="flex-1 px-3 sm:px-4 py-2 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 text-sm"
            onKeyDown={(e) => { if (e.key === 'Enter') onAddFeed(); }}
          />
          <button
            onClick={onAddFeed}
            className="px-3 sm:px-6 py-2 bg-black text-white font-semibold hover:bg-gray-800 dark:bg-white dark:text-black dark:hover:bg-gray-200 text-sm"
            aria-label={t.addFeed}
          >
            <Plus className="w-4 h-4 sm:mr-1" aria-hidden="true" />
            <span className="hidden sm:inline">{t.addFeed}</span>
          </button>
        </div>
      </div>

      <div className="bg-yellow-50 dark:bg-yellow-900/30 border-2 border-yellow-400 dark:border-yellow-600 p-3 sm:p-4 mb-4">
        <h3 className="font-bold mb-2 text-sm sm:text-base text-gray-900 dark:text-white">{t.exampleFeeds}</h3>
        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-3">{t.exampleFeedsNote}</p>
        {loadingFeeds ? (
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Loading...</p>
        ) : exampleFeeds.length > 0 ? (
          <>
            <div role="group" aria-label={t.exampleFeeds} className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {(showAllFeeds ? exampleFeeds : exampleFeeds.slice(0, 10)).map((feed) => {
                const alreadyAdded = subscribedUrls.has(feed.url.toLowerCase());
                const selected = selectedExampleUrls.has(feed.url) && !alreadyAdded;
                const tone = alreadyAdded
                  ? 'border-green-300 bg-green-50 text-green-700 dark:border-green-700 dark:bg-green-900/30 dark:text-green-300 cursor-default'
                  : selected
                    ? 'border-blue-600 bg-blue-50 text-blue-800 dark:border-blue-400 dark:bg-blue-900/40 dark:text-blue-100'
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-blue-700 dark:text-blue-400';
                const ariaLabel = alreadyAdded
                  ? `${feed.name} (${t.added})`
                  : `${selected ? t.onboardingDeselect : t.onboardingSelect}: ${feed.name}`;
                return (
                  <button
                    key={feed.url}
                    onClick={() => !alreadyAdded && toggleExample(feed.url)}
                    disabled={alreadyAdded}
                    aria-pressed={selected}
                    aria-label={ariaLabel}
                    className={`text-left px-3 py-2 border-2 text-xs sm:text-sm flex items-center justify-between gap-2 ${tone}`}
                  >
                    <span className="font-medium truncate">{feed.name}</span>
                    {alreadyAdded || selected ? (
                      <Check className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                    ) : (
                      <Plus className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                    )}
                  </button>
                );
              })}
            </div>
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
              <button
                onClick={commitSelected}
                disabled={selectedCount === 0}
                className="px-4 py-2 text-sm font-semibold bg-black text-white hover:bg-gray-800 dark:bg-white dark:text-black dark:hover:bg-gray-200 disabled:bg-gray-300 disabled:text-gray-500 dark:disabled:bg-gray-700 dark:disabled:text-gray-500 disabled:cursor-not-allowed"
              >
                {commitLabel}
              </button>
              {exampleFeeds.length > 10 && (
                <button
                  onClick={() => setShowAllFeeds(!showAllFeeds)}
                  className="text-xs sm:text-sm text-blue-700 dark:text-blue-400 hover:underline"
                >
                  {showAllFeeds ? t.showLess : `${t.showMore} (${exampleFeeds.length})`}
                </button>
              )}
            </div>
          </>
        ) : (
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">No feeds available</p>
        )}
      </div>

      {feeds.length > 0 && (
        <div className="bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 p-3 sm:p-4 mb-4">
          <h3 className="font-bold mb-3 text-sm sm:text-base text-gray-900 dark:text-white">{t.currentFeeds} ({feeds.length})</h3>
          <div className="space-y-3">
            {feeds.map((feed) => {
              const status = feedStatus[feed.name];
              const failed = isFeedFailed(status);
              const loading = isFeedLoading(status);

              return (
                <div
                  key={feed.url}
                  className={`border-2 p-3 ${
                    failed
                      ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20'
                      : 'border-gray-200 dark:border-gray-600'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white">
                        {feed.name}
                      </div>
                      <a
                        href={feed.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 flex items-center gap-1 break-all"
                      >
                        <ExternalLink className="w-3 h-3 flex-shrink-0" aria-hidden="true" />
                        <span className="truncate">{feed.url}</span>
                      </a>
                      {status && (
                        <div className={`text-xs mt-1 ${
                          failed
                            ? 'text-red-600 dark:text-red-400'
                            : status.startsWith('✓')
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-gray-500 dark:text-gray-400'
                        }`}>
                          {status}
                        </div>
                      )}
                      {feedLastRefresh[feed.name] && (
                        <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                          {t.lastRefresh}: {formatRelativeTime(feedLastRefresh[feed.name], now, t)}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* Reload button for failed feeds */}
                      {(failed || loading) && (
                        <button
                          onClick={() => onReloadFeed(feed)}
                          disabled={loading}
                          className={`p-2 rounded ${
                            loading
                              ? 'bg-gray-200 dark:bg-gray-600 text-gray-400 cursor-not-allowed'
                              : 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800'
                          }`}
                          aria-label={`Reload ${feed.name}`}
                          title={t.reloadFeed}
                        >
                          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
                        </button>
                      )}

                      {/* Remove button */}
                      <button
                        onClick={() => onRemoveFeed(feed.url)}
                        className="p-2 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-800 rounded"
                        aria-label={`Remove ${feed.name}`}
                        title={t.removeFeed}
                      >
                        <Trash2 className="w-4 h-4" aria-hidden="true" />
                      </button>
                    </div>
                  </div>

                  {/* Error guidance */}
                  {failed && (
                    <div className="mt-2 flex items-start gap-2 text-xs text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/40 p-2 rounded">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
                      <span>{t.feedFailedMessage}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {feedStatus['analysis'] && (
            <div className="text-xs text-blue-600 dark:text-blue-400 mt-3 font-semibold">
              {feedStatus['analysis']}
            </div>
          )}
          {feedStatus['autofill'] && (
            <div className="text-xs text-green-600 dark:text-green-400 mt-1 font-semibold">
              {feedStatus['autofill']}
            </div>
          )}
        </div>
      )}
    </>
  );
};
